// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tippy, { TippyProps } from "@tippyjs/react";
import { hideAll, Instance as TippyInstance } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import { TippyPopupStyle, tippyPopupStyles } from "@guestsupply/App/Components/Tippy/Style";
import { importTippyCssFiles } from "@guestsupply/App/Components/Tippy/tippy.helper";
import { useTippyProps } from "@guestsupply/App/Components/Tippy/tippy.hooks";
import xHeaderUpdate from "@guestsupply/App/Handlers/XNG/Header";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { addScrollbarGutter, getSystemZIndex, removeScrollbarGutter, xClassNames } from "@guestsupply/XDL/Utilities";
import { mergeWithCss } from "@guestsupply/XDL/Utilities/mergeWithCss";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import { injectCss } from "@insite/mobius/utilities";
import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

export const XngTippy = styled(Tippy)<TippyProps>`
    ${injectCss}
`;

export interface TippyPopupProps extends TippyProps {
    /**
     * Show or hide close button.
     */
    showCloseButton?: "both" | "mobile" | "desktop" | false;
    /**
     * Show tooltip as menu like MiniCart or as tooltip.
     */
    type?: "menu" | "tooltip";
    /**
     * Show tooltip as left-side or default on mobile.
     */
    mobileMode?: "left-side" | "default";
    /**
     * Event will be fired when popup open and close.
     */
    onTriggerPopup?: (isOpen: boolean) => void;
    /**
     * To remove span tag with element wrapper.
     */
    removeWrapper?: boolean;
    extendedStyles?: TippyPopupStyle;
    applyGuuter?: boolean;
}

type Props = TippyPopupProps;

const TippyPopup: FC<Props> = props => {
    const {
        type,
        onTriggerPopup: onTriggerPopupProps,
        showCloseButton,
        extendedStyles,
        children,
        removeWrapper,
        content: tippyContent,
        applyGuuter = true,
        ...otherProps
    } = props;

    const dispatch = useDispatch();
    const [styles] = useState(() => mergeWithCss(tippyPopupStyles, extendedStyles));
    const [instance, setInstance] = useState<TippyInstance>();
    const options = useTippyProps(otherProps);
    const { headerHeight } = useAppSelector(state => ({
        headerHeight: (state as CustomAppState).xHeader.stickyHeader || "58",
    }));

    useEffect(() => {
        importTippyCssFiles();
    }, []);

    useEffect(() => {
        if (instance && otherProps.mobileMode === "left-side") {
            if (instance.popper.style.getPropertyValue("--tippy-top-offset") !== headerHeight) {
                const height = parseInt(headerHeight || "58", 10);
                instance.popper.className = xClassNames.append("tippy-menu-left-side", instance.popper.className);
                // subtract the header margin buttom
                instance.popper.style.setProperty("--tippy-top-offset", `${height}px`);
            }
        }
    }, [headerHeight, instance]);

    const closeButtonHandler = () => {
        instance?.hide?.();
    };

    const onCreateHandler = (instance: TippyInstance) => {
        setInstance(instance);
        otherProps.onCreate?.(instance);
    };

    const onShowHandler = (instance: TippyInstance) => {
        /**
         * Close all Tippy instances
         */
        hideAll();

        /**
         * Show overlay menu
         */
        dispatch(xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_popup` }));

        /**
         * Add overflow: hidden for body and add scrollbar-gutter for html.
         * we need it to prevent page scroll when tippy opened
         */
        if (applyGuuter) {
            addScrollbarGutter();
            ariaAppHider.hide(document.body);
        }

        otherProps.onShow?.(instance);
        onTriggerPopupProps?.(true);
        // XNG-CHANGES : close dropdowns when tippy opened
        document.querySelectorAll("select").forEach(select => select.blur());
    };

    const onShownHandler = (instance: TippyInstance) => {
        if (type === "menu") {
            instance.popper.className = xClassNames.append("menu-opened", instance.popper.className);
        }

        otherProps.onShown?.(instance);
    };

    const onHideHandler = (instance: TippyInstance) => {
        if (type === "menu") {
            instance.popper.className = xClassNames.remove("menu-opened", instance.popper.className);
        }

        /**
         * Hide overlay menu
         */
        dispatch(xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_popup` }));

        /**
         * Remove overflow: hidden from body and add scrollbar-gutter from html.
         * we need it to make page scrollable after tippy closed
         */
        if (applyGuuter) {
            removeScrollbarGutter();
            ariaAppHider.show(document.body);
        }

        otherProps.onHide?.(instance);
        onTriggerPopupProps?.(false);
    };

    return (
        <>
            <XngTippy
                {...options}
                onCreate={onCreateHandler}
                onShow={onShowHandler}
                onShown={onShownHandler}
                onHide={onHideHandler}
                zIndex={getSystemZIndex("tippy")}
                content={
                    <StyledWrapper className="content-wrapper">
                        <Icon
                            {...styles.closeButton}
                            className={`tippy-close-icon ${showCloseButton || ""}`}
                            onClick={closeButtonHandler}
                            role="button"
                            aria-label="Tooltip Close Button"
                        />
                        {tippyContent}
                    </StyledWrapper>
                }
                {...styles.main}
            >
                {removeWrapper ? children : <span style={styles.elementWrapper}>{children}</span>}
            </XngTippy>
        </>
    );
};

TippyPopup.defaultProps = {
    showCloseButton: false,
    mobileMode: "default",
    type: "tooltip",
};

export default TippyPopup;
